import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { clearPersistedSupabaseSession, supabase } from '../lib/supabaseClient';
import {
  clearDesignAccessToken,
  consumeDesignBootstrapFromWindowName,
  discoverDesignBootstrap,
  exchangeSupabaseSessionForBff,
  resumeWorkforceBffSession,
  signOutWorkforceBff,
} from '../lib/workforceBff';
import { safeReturnTo } from '../auth/returnTo';

const AuthContext = createContext(null);
// The BFF session probe has no timeout of its own; never hold the splash longer than this.
const BFF_RESUME_WAIT_MS = 10000;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  // A failed profile read and a user who really is awaiting approval both leave
  // profile null, and resolveHome sends null to /pending. Migration 0131 turned
  // that ambiguity into an outage: it revoked EXECUTE on private.is_admin, the
  // profiles SELECT policy calls it, so every read returned "permission denied"
  // and every user - super_admin included - was shown «در انتظار تأیید» with no
  // hint that anything had failed. Carry the reason so /pending can tell the
  // truth instead of inventing a business state out of a transport error.
  const [profileError, setProfileError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track the last user we resolved for so we can skip the loading flash when
  // supabase-js rebroadcasts SIGNED_IN on tab focus (a known v2 behaviour). Without
  // this, switching tabs and coming back unmounts the entire admin tree via
  // <Splash/> and any in-progress work in InspectionTab is lost.
  const lastUserIdRef = useRef(null);
  const bffModeRef = useRef(false);
  const designTokenModeRef = useRef(false);
  // Settles once the BFF cookie probe has answered (or given up). Until then an
  // empty Supabase INITIAL_SESSION must not end loading: on a reload the guards
  // would send the user to /login, and the late BFF identity then to the default tab.
  const bffSettledRef = useRef(null);

  const applyBffIdentity = useCallback((payload, { design = false } = {}) => {
    if (!payload?.profile?.id) return false;
    bffModeRef.current = true;
    designTokenModeRef.current = design;
    lastUserIdRef.current = payload.profile.id;
    setSession({ user: { id: payload.profile.id, email: payload.profile.email || null } });
    setProfile(payload.profile);
    setProfileError(null);
    setLoading(false);
    return true;
  }, []);

  const loadProfile = useCallback(async (userId, isActive = () => true) => {
    if (!userId) { if (isActive()) { setProfile(null); setProfileError(null); } return; }
    const { data, error } = await supabase
      .from('profiles')
      .select('id, email, full_name, phone, role, status')
      .eq('id', userId)
      .maybeSingle();
    if (!isActive()) return;
    if (error) {
      // eslint-disable-next-line no-console
      console.error('loadProfile error', error.message);
      setProfile(null);
      setProfileError(error.message || 'خطای نامشخص');
    } else {
      setProfile(data);
      // A signed-in user with no row is not a read failure, but it is not a
      // pending account either; the signup trigger did not run. Say so.
      setProfileError(data ? null : 'PROFILE_ROW_MISSING');
    }
  }, []);

  useEffect(() => {
    let active = true;

    const designBootstrap = consumeDesignBootstrapFromWindowName();
    let settleBff;
    const settled = new Promise((resolve) => { settleBff = resolve; });
    const timer = setTimeout(() => settleBff(), BFF_RESUME_WAIT_MS);
    bffSettledRef.current = settled.then(() => clearTimeout(timer));
    const resumeAdminSession = () => resumeWorkforceBffSession().then((payload) => {
      if (active && payload) applyBffIdentity(payload);
    }).catch(() => {
      // Flag-off, old-host and temporary BFF outages all retain the existing
      // Supabase localStorage bearer flow during the overlap.
    }).finally(() => settleBff());

    if (designBootstrap?.profile) {
      applyBffIdentity(designBootstrap, { design: true });
      settleBff();
    } else if (typeof window !== 'undefined' && window.location.pathname.startsWith('/design')) {
      discoverDesignBootstrap().then((config) => {
        if (active && config?.enabled && config.admin_origin) {
          window.location.assign(config.admin_origin + '/api/workforce/design/authorize');
        } else {
          resumeAdminSession();
        }
      }).catch(resumeAdminSession);
    } else {
      resumeAdminSession();
    }

    // The onAuthStateChange callback runs while GoTrue holds its auth lock.
    // Awaiting another Supabase call (loadProfile -> from('profiles')) inside it
    // deadlocks on INITIAL_SESSION (full reload with a persisted session).
    // Keep the callback synchronous; defer the profile load outside the lock.
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (!active || bffModeRef.current) return;
      const newUserId = newSession?.user?.id || null;
      const sameUser = lastUserIdRef.current && newUserId && lastUserIdRef.current === newUserId;
      setSession(newSession);
      // When identity is (re)established we don't have the profile yet; stay in
      // loading so guards show the splash instead of routing on a null profile
      // (which resolveHome maps to /pending). Exclude TOKEN_REFRESHED/USER_UPDATED
      // so periodic background refreshes don't flash the splash. CRITICAL: also
      // exclude rebroadcast of SIGNED_IN for the same user (Supabase fires this on
      // tab focus) — otherwise every focus event re-mounts the admin tree.
      if (newSession && !sameUser && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        setLoading(true);
      }
      lastUserIdRef.current = newUserId;
      setTimeout(async () => {
        if (!active) return;
        // A BFF cookie identity, when there is one, wins over an empty Supabase session.
        await bffSettledRef.current;
        if (!active || bffModeRef.current) return;
        // Skip the profile reload too when the user didn't change — the cached
        // profile is still valid and re-fetching it momentarily nulls profile
        // state in flight, which downstream guards interpret as "not approved".
        if (!sameUser) {
          await loadProfile(newSession?.user?.id, () => active);
        }
        if (active) setLoading(false);
      }, 0);
    });
    return () => { active = false; clearTimeout(timer); settleBff(); sub.subscription.unsubscribe(); };
  }, [applyBffIdentity, loadProfile]);

  const signIn = async ({ email, password, returnTo }) => {
    const result = await supabase.auth.signInWithPassword({ email, password });
    const signedInSession = result?.data?.session;
    if (!result.error && signedInSession) {
      const bff = await exchangeSupabaseSessionForBff(signedInSession).catch(() => null);
      if (bff && applyBffIdentity(bff)) {
        // Local-only removal leaves the server-held BFF credential usable and
        // removes the routine workforce bearer from browser storage.
        clearPersistedSupabaseSession();
        // A top-level reload discards supabase-js's in-memory copy too. The new
        // document resumes solely from the host-only BFF cookie.
        if (typeof window !== 'undefined') window.location.replace(safeReturnTo(returnTo) || '/');
      }
    }
    return result;
  };

  const signOut = async () => {
    if (bffModeRef.current) {
      if (designTokenModeRef.current) clearDesignAccessToken();
      else await signOutWorkforceBff();
      clearPersistedSupabaseSession();
      bffModeRef.current = false;
      designTokenModeRef.current = false;
      setSession(null);
      setProfile(null);
      setProfileError(null);
      return;
    }
    await supabase.auth.signOut();
  };

  const refreshProfile = async () => {
    if (bffModeRef.current) {
      if (designTokenModeRef.current) return profile;
      const payload = await resumeWorkforceBffSession().catch(() => null);
      if (payload) applyBffIdentity(payload);
      return payload?.profile || null;
    }
    return loadProfile(session?.user?.id);
  };

  const value = {
    session,
    profile,
    profileError,
    loading,
    refreshProfile,
    signUp: ({ email, password, fullName, phone }) =>
      supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName, phone: phone || null } },
      }),
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
